'use client';

import { useEffect, useState, type ReactNode } from 'react';
import { NextIntlClientProvider } from 'next-intl';
import { defaultLocale, locales, isRtl, type Locale } from '@/i18n/config';
import enMessages from '@/messages/en.json';
import heMessages from '@/messages/he.json';

function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (locales as readonly string[]).includes(value);
}

/* A logged-in user's stored preference always wins when present — that is
 * the authenticated behaviour this must not disturb. A public share-page
 * visitor has no stored preference by definition (empty localStorage), so
 * for them resolution falls through to the share URL's own `?lang=` param
 * and then to the browser's language, rather than always landing on
 * `defaultLocale` regardless of who the link was sent to. */
function resolveLocale(): Locale {
  if (typeof window === 'undefined') return defaultLocale;

  try {
    const raw = window.localStorage.getItem('user-preferences');
    if (raw) {
      const lang = JSON.parse(raw)?.language;
      if (isLocale(lang)) return lang;
    }
  } catch {
    // ignore parse / storage errors and fall through
  }

  const langParam = new URLSearchParams(window.location.search).get('lang');
  if (isLocale(langParam)) return langParam;

  const navLang = window.navigator.language?.slice(0, 2).toLowerCase();
  if (isLocale(navLang)) return navLang;

  return defaultLocale;
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocale] = useState<Locale>(defaultLocale);
  const messages = locale === 'he' ? heMessages : enMessages;

  useEffect(() => {
    setLocale(resolveLocale());
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.lang = locale;
    root.dir = isRtl(locale) ? 'rtl' : 'ltr';
  }, [locale]);

  return (
    <NextIntlClientProvider locale={locale} messages={messages}>
      {children}
    </NextIntlClientProvider>
  );
}
